import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
    viewChildren,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {catchError, map, Observable, of, startWith, switchMap} from 'rxjs';
import {DateTimeFormatOptions} from 'luxon';
import {DatePipe, LoadComponent, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    dateFormatOptions,
    downloadCsv,
    formatStreetAddress,
    keyComparator,
    numericLocaleComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {InstallationProjectsService, isNotFound} from '../installation-projects.service';
import {
    INSTALLATION_PROJECT_VISIT_STATUSES,
    formatVisitTimeSlot,
    InstallationProjectLocation,
    InstallationProjectStatusDisplay,
    installationProjectVisitStatusDisplay,
} from '../installation-projects.model';

type LocationsView =
    | {status: 'loading'}
    | {status: 'notFound'}
    | {status: 'error'}
    | {status: 'success'; locations: InstallationProjectLocation[]};

type LocationRow = {
    location: InstallationProjectLocation;
    address: string;
    timeSlot: string | null;
    statusDisplay: InstallationProjectStatusDisplay;
};

@Component({
    selector: 'app-installation-project-locations',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, LoadComponent, DatePipe, SortableTableHeaderComponent],
    templateUrl: './installation-project-locations.component.html',
    styleUrl: './installation-project-locations.component.scss',
})
export class InstallationProjectLocationsComponent {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    private readonly installationProjectsService = inject(InstallationProjectsService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly translateService = inject(TranslateService);

    /** Column order of the table; `column` doubles as sort key. Every column sorts. */
    protected readonly columns = [
        {column: 'proBraLoc', labelKey: 'properties_proBraLoc', module: 'systems'},
        {column: 'address', labelKey: 'installationProjects_column_address', module: 'ngonline'},
        {
            column: 'consumerName',
            labelKey: 'installationProjects_locations_column_consumer',
            module: 'ngonline',
        },
        {
            column: 'email',
            labelKey: 'installationProjects_locations_column_email',
            module: 'ngonline',
        },
        {
            column: 'visitDate',
            labelKey: 'installationProjects_locations_column_visitDate',
            module: 'ngonline',
        },
        {
            column: 'timeSlot',
            labelKey: 'installationProjects_locations_column_timeSlot',
            module: 'ngonline',
        },
        {column: 'visitStatus', labelKey: 'installationProjects_column_status', module: 'ngonline'},
        {
            column: 'remarks',
            labelKey: 'installationProjects_locations_column_remarks',
            module: 'ngonline',
        },
    ] as const;

    /** The mock renders visit dates as weekday + numeric date (e.g. "Sat. 07.03.2026"). */
    protected readonly visitDateFormat: DateTimeFormatOptions = {
        weekday: 'short',
        ...dateFormatOptions,
    };

    protected readonly view = toSignal(
        // The project id lives on the parent `:projectId` segment; a tab child with its own path
        // does not inherit it (the router's default emptyOnly strategy).
        (this.activatedRoute.parent ?? this.activatedRoute).paramMap.pipe(
            map(params => params.get('projectId')),
            switchMap(projectId =>
                projectId == null
                    ? of<LocationsView>({status: 'notFound'})
                    : this.loadLocations(projectId),
            ),
        ),
        {initialValue: {status: 'loading'}},
    );

    private readonly allRows = computed<LocationRow[]>(() => {
        const view = this.view();
        if (view.status !== 'success') {
            return [];
        }
        return view.locations.map(location => ({
            location,
            address: formatStreetAddress({
                address1: location.street,
                address2: null,
                postalCode: location.postalCode,
                city: location.city,
            }),
            timeSlot: formatVisitTimeSlot(location.startTime, location.endTime),
            statusDisplay: installationProjectVisitStatusDisplay(location.visitStatus),
        }));
    });

    /** Unsorted keeps the backend order: visit date ascending, unscheduled locations last. */
    protected readonly sorting = signal<Sorting | null>(null);

    protected readonly rows = computed<LocationRow[]>(() => {
        const sorting = this.sorting();
        if (sorting === null) {
            return this.allRows();
        }
        return [...this.allRows()].sort(rowComparator(sorting));
    });

    protected onSort(sorting: Sorting): void {
        // Each header owns its own arrow state, so the previously sorted one has to be cleared
        // explicitly or two columns show an arrow at once.
        this.headers().forEach(header => {
            header.direction = header.column === sorting.column ? sorting.direction : null;
        });
        this.sorting.set(sorting);
    }

    /** Exports the rows as the user sees them - same columns, same sort order. */
    protected exportLocations(): void {
        const headers = this.columns.map(column =>
            this.translateService.translate(column.labelKey, 'online', column.module),
        );
        downloadCsv('installation-project-locations', [
            headers,
            ...this.rows().map(row => [
                row.location.proBraLoc ?? null,
                row.address,
                row.location.consumerName,
                row.location.email,
                row.location.visitDate?.toFormat('dd.MM.yyyy') ?? null,
                row.timeSlot,
                this.translateService.translate(row.statusDisplay.labelKey, 'online', 'ngonline'),
                row.location.remarks,
            ]),
        ]);
    }

    private loadLocations(projectId: string): Observable<LocationsView> {
        return this.installationProjectsService.getInstallationProjectLocations(projectId).pipe(
            map((locations): LocationsView => ({status: 'success', locations})),
            startWith<LocationsView>({status: 'loading'}),
            catchError(failure =>
                of<LocationsView>(isNotFound(failure) ? {status: 'notFound'} : {status: 'error'}),
            ),
        );
    }
}

function rowComparator(sorting: Sorting): (a: LocationRow, b: LocationRow) => number {
    const order = sorting.direction;
    switch (sorting.column) {
        case 'address':
            return caseInsensitiveComparator(row => row.address, order);
        case 'consumerName':
            return caseInsensitiveComparator(row => row.location.consumerName ?? '', order);
        case 'email':
            return caseInsensitiveComparator(row => row.location.email ?? '', order);
        case 'visitDate':
            return keyComparator(row => row.location.visitDate?.toMillis() ?? null, order);
        case 'timeSlot':
            return numericLocaleComparator(row => row.timeSlot ?? '', order);
        case 'visitStatus':
            return keyComparator(
                row => INSTALLATION_PROJECT_VISIT_STATUSES.indexOf(row.location.visitStatus),
                order,
            );
        case 'remarks':
            return caseInsensitiveComparator(row => row.location.remarks ?? '', order);
        default:
            return numericLocaleComparator(row => row.location.proBraLoc ?? '', order);
    }
}
